import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";

import LocationModal from "../modals/LocationModal";
import LocationsMapModal from "../modals/LocationsMapModal";
import LocationImport from "./LocationImport";
import LocationUpdateImport from "./LocationUpdateImport";
import LocationImportBatches from "./LocationImportBatches";
import LocationImportReview from "./LocationImportReview";
import Page from "./Page";
import EntityList from "./EntityList";
import Filter from "../ui/Filter";
import Pagination from "../elements/Pagination";
import Dialog_Flex from "../ui/Dialog_Flex";

import useLocalization from "../../hooks/useLocalization";
import useEntity from "../../hooks/useEntity";
import useBrowser from "../../hooks/useBrowser";
import useWork from "../../hooks/useWork";
import usePage from "../../hooks/usePage";
import useData from "../../hooks/useData";
import { getId } from "../../utils/idHelpers";

const Locations = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { submitWork } = useWork();
  const { showMessage } = usePage();
  const { windowHeight } = useBrowser();
  const { getLabel, getTitle, getMessage } = useLocalization();
  const { executeAction } = useData();

  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [openUpdateImportModal, setOpenUpdateImportModal] = useState(false);
  const [openBatchesModal, setOpenBatchesModal] = useState(false);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filter, setFilter] = useState({ status: "open" });
  const [locationId, setLocationId] = useState(null);
  const [orderBy, setOrderBy] = useState("address.city");
  const [order, setOrder] = useState("asc");

  const [selectedItems, setSelectedItems] = useState([]);

  const [openMissingDialog, setOpenMissingDialog] = useState(false);
  const [missingLocations, setMissingLocations] = useState([]);
  const [missingSelected, setMissingSelected] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);

  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [openMapModal, setOpenMapModal] = useState(false);
  const [mapLocationIds, setMapLocationIds] = useState([]);
  const [returnUrl, setReturnUrl] = useState(null);
  const [returnState, setReturnState] = useState(null);

  const userId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  const {
    getPage,
    setEntity,
    entityName,
    entityList,
    entitySchema,
    recordCount,
    ids,
  } = useEntity();

  useEffect(() => {
    setEntity("Location");
    if (location && location.state) {
      if (location.state.filter) {
        setFilter(location.state.filter);
      }
      if (location.state.pageNumber !== undefined) {
        setPageNumber(location.state.pageNumber);
      }
      if (location.state.recordsPerPage !== undefined) {
        setRecordsPerPage(location.state.recordsPerPage);
      }
      if (location.state.orderBy) {
        setOrderBy(location.state.orderBy);
      }
      if (location.state.order) {
        setOrder(location.state.order);
      }
      if (location.state.selectedItems) {
        setSelectedItems(location.state.selectedItems);
      }
      if (location.state.returnUrl) {
        setReturnUrl(location.state.returnUrl);
      }
      if (location.state.returnState) {
        setReturnState(location.state.returnState);
      }
    }
  }, []);

  // Dynamically size rows per page based on viewport height so the table fills available space.
  useEffect(() => {
    if (!windowHeight) return;

    const CHROME_HEIGHT = 260; // matches tableHeight calculation below
    const HEADER_HEIGHT = 48;
    const ROW_HEIGHT = 44;
    const VALID_OPTIONS = [10, 25, 50, 100];

    const availableHeight = Math.max(200, windowHeight - CHROME_HEIGHT);
    const bodyHeight = Math.max(100, availableHeight - HEADER_HEIGHT);
    const computed = Math.max(
      10,
      Math.min(100, Math.floor(bodyHeight / ROW_HEIGHT)),
    );

    // Round to nearest valid option
    const computedRows = VALID_OPTIONS.reduce((prev, curr) => {
      return Math.abs(curr - computed) < Math.abs(prev - computed)
        ? curr
        : prev;
    });

    if (computedRows !== recordsPerPage) {
      setRecordsPerPage(computedRows);
      setPageNumber(0);
    }
  }, [windowHeight]);

  useEffect(() => {
    if (!entityName) return;
    getPage(pageNumber, recordsPerPage, filter, orderBy, order);
  }, [entityName]);

  useEffect(() => {
    if (!entityName) return;
    getPage(pageNumber, recordsPerPage, filter, orderBy, order);
  }, [entityName, pageNumber, recordsPerPage, orderBy, order]);

  // When filter changes, reset to page 0 and fetch
  useEffect(() => {
    if (!entityName) return;
    setPageNumber(0);
    getPage(0, recordsPerPage, filter, orderBy, order);
  }, [filter]);

  const loadMissingLocations = async () => {
    setMissingLoading(true);
    try {
      const results = await executeAction({
        entityName: "Location",
        action: "getMissingCoordinates",
      });

      if (results?.isSuccessful) {
        setMissingLocations(results.data || []);
      } else {
        setMissingLocations([]);
      }
      setMissingSelected([]);
    } catch (err) {
      setMissingLocations([]);
      setMissingSelected([]);
    } finally {
      setMissingLoading(false);
    }
  };

  const handleMapSelected = () => {
    if (selectedItems.length === 0) return;
    setMapLocationIds(selectedItems);
    setOpenMapModal(true);
  };

  const handleMapLocation = (locationId) => {
    setMapLocationIds([locationId]);
    setOpenMapModal(true);
  };

  const handleToolbarItemClick = async (item) => {
    switch (item) {
      case "mapSelected":
        handleMapSelected();
        break;

      case "back":
        if (returnUrl) {
          navigate(returnUrl, { state: returnState });
        } else {
          navigate("/clients");
        }
        break;

      case "add":
        setLocationId(null);
        setOpenLocationModal(true);
        break;

      case "import":
        setOpenImportModal(true);
        break;

      case "updateImport":
        setOpenUpdateImportModal(true);
        break;

      case "batches":
        setOpenBatchesModal(true);
        break;

      case "refresh":
        setSelectedItems([]);
        break;

      case "missingCoords":
        await loadMissingLocations();
        setOpenMissingDialog(true);
        break;

      default:
        if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
          showMessage("No locations selected");
          return;
        }

        const selection = await executeAction({
          entityName: "WorkQueue",
          action: "createSelection",
          data: {
            workType: "exportLocations",
            ids: selectedItems,
            userId,
            email,
          },
          displayMessage: false,
        });

        if (selection?.data?.selectionId) {
          navigate("/selectedlocations", {
            state: {
              selectionId: selection.data.selectionId,
              returnUrl: returnUrl,
              returnState: returnState,
              locationsState: {
                filter,
                pageNumber,
                recordsPerPage,
                orderBy,
                order,
                selectedItems,
                returnUrl,
                returnState,
              },
            },
          });
        } else {
          showMessage("Unable to create selection");
        }
    }
  };

  const handleSearch = async () => {
    setPageNumber(0);
    await getPage(0, recordsPerPage, filter, orderBy, order);
  };

  const handleClearFilter = async () => {
    setFilter(null);
    setPageNumber(0);
    setSelectedItems([]);
    await getPage(0, recordsPerPage, null, orderBy, order);
  };

  const handleCloseModal = () => {
    setOpenLocationModal(false);
    setLocationId(null);
  };

  const handleLocationSave = () => {
    setOpenLocationModal(false);
    setLocationId(null);
    // Refresh the location list
    getPage(pageNumber, recordsPerPage, filter, orderBy, order);
  };

  const handleRowClick = (id) => {
    if (!id) return;

    setLocationId(id);
    setOpenLocationModal(true);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
  };

  const handleSortChange = (columnName, direction) => {
    setPageNumber(0);
    setOrderBy(columnName);
    setOrder(direction);
  };

  const normalizeIds = (source) => {
    if (!Array.isArray(source) || source.length === 0) return [];

    return source
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return item;
        return getId(item);
      })
      .filter(Boolean)
      .map((id) => id.toString());
  };

  const getFilteredIds = async () => {
    let filteredIds = normalizeIds(ids);

    if (recordCount && filteredIds.length < recordCount) {
      const results = await executeAction({
        entityName: "Location",
        action: "getPage",
        filter: filter || null,
        orderBy: orderBy || null,
        order: order || null,
        data: {
          pageNumber: 0,
          recordsPerPage: recordCount,
          orderBy: orderBy || null,
          order: order || null,
        },
        displayMessage: false,
      });

      if (results?.isSuccessful) {
        if (Array.isArray(results.ids) && results.ids.length > 0) {
          filteredIds = normalizeIds(results.ids);
        } else if (Array.isArray(results.data) && results.data.length > 0) {
          filteredIds = normalizeIds(results.data);
        }
      }
    }

    return filteredIds;
  };

  const handleSelectAll = async (checked) => {
    const filteredIds = await getFilteredIds();

    if (filteredIds.length === 0) return;

    if (checked) {
      setSelectedItems((current) => {
        const merged = new Set([...(current || []), ...filteredIds]);
        return Array.from(merged);
      });
    } else {
      setSelectedItems((current) => {
        const selected = new Set((current || []).map((id) => id.toString()));
        for (const id of filteredIds) {
          selected.delete(id);
        }
        return Array.from(selected);
      });
    }
  };

  const handleSelect = (id, checked) => {
    setSelectedItems((current) => {
      let newItems = [...current];

      if (checked) {
        if (!newItems.includes(id)) {
          newItems.push(id);
        }
      } else {
        const currentIndex = newItems.findIndex((item) => item === id);
        if (currentIndex > -1) {
          newItems.splice(currentIndex, 1);
        }
      }

      return newItems;
    });
  };

  const handleMissingSelectAll = (checked) => {
    if (!missingLocations || missingLocations.length === 0) return;
    setMissingSelected(checked ? missingLocations.map((loc) => loc.id) : []);
  };

  const handleMissingSelect = (id, checked) => {
    setMissingSelected((current) => {
      let newItems = [...current];

      if (checked) {
        if (!newItems.includes(id)) {
          newItems.push(id);
        }
      } else {
        const currentIndex = newItems.findIndex((item) => item === id);
        if (currentIndex > -1) {
          newItems.splice(currentIndex, 1);
        }
      }

      return newItems;
    });
  };

  const handleMissingDialogAction = async (action) => {
    switch (action) {
      case "cancel":
        setOpenMissingDialog(false);
        setMissingSelected([]);
        break;

      case "apply":
        try {
          const results = await executeAction({
            entityName: "Location",
            action: "geocodeLocations",
            data: {
              locationIds: missingSelected,
            },
            displayMessage: true,
          });

          if (results?.isSuccessful) {
            showMessage("Geocode job queued");
          } else {
            showMessage("Unable to queue geocode job");
          }
        } catch (err) {
          showMessage("Unable to queue geocode job");
        }

        setOpenMissingDialog(false);
        setMissingSelected([]);
        break;
    }
  };

  return (
    <>
      <Page
        pageName="locations"
        onToolItemClick={handleToolbarItemClick}
        includeToolbar
        items={[
          ...(returnUrl ? [{ name: "back", iconName: "ArrowBack" }] : []),
          { name: "add", iconName: "AddCircleOutline" },
          { name: "import", iconName: "Upload" },
          { name: "updateImport", label: "update", iconName: "UploadFile" },
          {
            name: Array.isArray(selectedItems) ? selectedItems.length : 0,
            iconName: "Download",
          },
          {
            name: "mapSelected",
            iconName: "Map",
            disabled: selectedItems.length === 0,
          },
          { name: "batches", iconName: "FolderOpen" },
          { name: "missingCoords", iconName: "GpsOff" },
          { name: "refresh", iconName: "Refresh" },
        ]}
      >
        <Filter
          fields={[
            "retailer",
            "locationId",
            "banner",
            "locationNumber",
            "city",
            "province",
            "syndication",
            "syndicatedTerritories",
            "locationStatus",
          ]}
          fieldRows={[
            ["retailer", "banner", "locationNumber", "locationId"],
            [
              "province",
              "city",
              "syndicatedTerritories",
              "syndication",
              "locationStatus",
            ],
          ]}
          filter={filter}
          onFilterChange={(newValue) => handleFilterChange(newValue)}
          onSearch={handleSearch}
          onClear={handleClearFilter}
        />

        <Pagination
          totalCount={recordCount}
          styles={{ marginTop: "5px" }}
          pageNumber={pageNumber}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
        />

        <EntityList
          entityList={entityList}
          entitySchema={entitySchema}
          pipeline="list"
          onRowClick={handleRowClick}
          tableHeight={`${windowHeight - 260}px`}
          allowSelect
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          columns={[
            { name: "id", label: "ID", colIndex: 0 },
            { name: "name", colIndex: 1 },
            { name: "locationNumber", colIndex: 2 },
            { name: "address.streetAddress", colIndex: 3 },
            { name: "address.city", colIndex: 4 },
            { name: "address.province", colIndex: 5 },
            { name: "syndicatedTerritory", colIndex: 6 },
            {
              name: "actions",
              label: "actions",
              width: "110px",
              sortable: false,
              render: (row) => (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleRowClick(row.id)}
                    title="Edit Location"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {row.address?.latitude && row.address?.longitude && (
                    <IconButton
                      size="small"
                      onClick={() => handleMapLocation(row.id)}
                      title="Map Location"
                    >
                      <MapIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ),
            },
          ]}
          selectedItems={selectedItems}
          onSortChange={handleSortChange}
          orderBy={orderBy}
          order={order}
        />
      </Page>

      {openMissingDialog ? (
        <Dialog_Flex
          title={getTitle("geocodeMissing")}
          message={getMessage("geocodeMissing")}
          open={openMissingDialog}
          onClose={() => setOpenMissingDialog(false)}
          actions={[
            { name: "cancel", label: "cancel" },
            { name: "apply", label: "apply" },
          ]}
          onAction={handleMissingDialogAction}
        >
          <Box
            padding="20px"
            sx={{ width: 520 }}
          >
            {missingLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 120,
                }}
              >
                <CircularProgress />
              </Box>
            ) : missingLocations.length === 0 ? (
              <Typography color="textSecondary">
                {getMessage("noRecords")}
              </Typography>
            ) : (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        missingLocations.length > 0 &&
                        missingSelected.length === missingLocations.length
                      }
                      onChange={(event) =>
                        handleMissingSelectAll(event.target.checked)
                      }
                    />
                  }
                  label="Select All"
                />
                <Box
                  sx={{
                    maxHeight: 320,
                    overflow: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  <List dense>
                    {missingLocations.map((location) => {
                      const address = location.address || {};
                      const addressText = [
                        address.streetAddress,
                        address.city,
                        address.province,
                        address.postal,
                      ]
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <ListItem
                          key={location.id}
                          disableGutters
                          sx={{ px: 1 }}
                        >
                          <Checkbox
                            checked={missingSelected.includes(location.id)}
                            onChange={(event) =>
                              handleMissingSelect(
                                location.id,
                                event.target.checked,
                              )
                            }
                          />
                          <ListItemText
                            primary={location.name}
                            secondary={addressText || "No address"}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Box>
            )}
          </Box>
        </Dialog_Flex>
      ) : null}

      <LocationModal
        key={locationId || "new-location"}
        open={openLocationModal}
        onClose={handleCloseModal}
        onSave={handleLocationSave}
        locationId={locationId}
      />

      {openImportModal && (
        <LocationImport
          onClose={() => setOpenImportModal(false)}
          onComplete={async () => {
            setOpenImportModal(false);
            setPageNumber(0);
            await getPage(0, recordsPerPage, filter, orderBy, order);
          }}
        />
      )}

      {openUpdateImportModal && (
        <LocationUpdateImport
          open={openUpdateImportModal}
          onClose={() => setOpenUpdateImportModal(false)}
          onQueued={() => {
            setOpenUpdateImportModal(false);
          }}
        />
      )}

      {openBatchesModal && (
        <LocationImportBatches
          open={openBatchesModal}
          onClose={() => setOpenBatchesModal(false)}
          onSelectBatch={async (batch) => {
            setSelectedBatch(batch);
            setOpenReviewModal(true);
            setOpenBatchesModal(false);
          }}
        />
      )}

      {openReviewModal && (
        <LocationImportReview
          open={openReviewModal}
          batch={selectedBatch}
          onClose={() => {
            setOpenReviewModal(false);
            setSelectedBatch(null);
          }}
          onCompleted={async () => {
            setPageNumber(0);
            await getPage(0, recordsPerPage, filter, orderBy, order);
          }}
        />
      )}

      <LocationsMapModal
        open={openMapModal}
        onClose={() => setOpenMapModal(false)}
        locationIds={mapLocationIds}
      />
    </>
  );
};

export default Locations;
